import React from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const ServiceCard = ({ title, description, image, features }) => (
  <div className="relative group">
    {/* Image Section */}
    <div className="relative aspect-[16/10] overflow-hidden rounded-2xl mb-8">
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500" />
      <img 
        src={image} 
        alt={title}
        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
      />
      
      {/* Decorative Circle */}
      <div className="absolute -right-12 -top-12 w-[300px] h-[300px] border-2 border-[#64D652] rounded-full opacity-20" />
    </div>

    {/* Content Section */}
    <div className="space-y-4">
      <h3 className="text-3xl font-light text-neutral-800">{title}</h3>
      <p className="text-lg text-neutral-600 leading-relaxed">{description}</p>
      
      {/* Features List */}
      <ul className="space-y-3 mt-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center space-x-3 text-neutral-700">
            <svg className="w-5 h-5 text-[#64D652]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ServicesSection = () => {
  const services = [
    {
      title: "Personalized Wellness Assessment",
      description: "Comprehensive evaluation using advanced diagnostic tools to create your unique wellness profile.",
      image: "https://images.unsplash.com/photo-1571019613576-2b22c76fd955?auto=format&fit=crop&q=80",
      features: [
        "AI-powered health analysis",
        "Lifestyle and habit assessment",
        "Personalized wellness scoring",
        "Detailed health insights report"
      ]
    },
    {
      title: "Holistic Therapy Sessions",
      description: "Expert-led therapeutic sessions combining traditional wisdom with modern techniques.",
      image: "https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&q=80",
      features: [
        "Traditional healing practices",
        "Modern therapeutic methods",
        "Stress reduction techniques",
        "Energy balancing sessions"
      ]
    },
    {
      title: "Nutrition & Lifestyle Coaching",
      description: "Tailored guidance for optimal nutrition and lifestyle modifications to enhance your wellbeing.",
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80",
      features: [
        "Personalized meal planning",
        "Dietary recommendations",
        "Lifestyle optimization",
        "Progress tracking"
      ]
    }
  ];

  return (
    <section className="relative py-24 bg-[#e8e3df] overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-24" data-aos="fade-up">
          <div className="text-sm text-neutral-600 mb-4">
            Our Services
          </div>
          <h2 className="text-6xl font-light mb-6 text-neutral-800">
            Premium Wellness Services
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Experience our comprehensive range of wellness services, each designed to
            enhance your journey towards optimal health and wellbeing.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
          {services.map((service, index) => (
            <div key={index} data-aos="fade-up" data-aos-delay={index * 100}>
              <ServiceCard {...service} />
            </div>
          ))}
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiM2NGQ2NTIiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-30" />
      </div>
    </section>
  );
};

export default ServicesSection; 